import React, {
  FunctionComponent,
  useCallback,
  useContext,
  useState,
} from 'react';

import { useFunctionOnEscape } from '../../hooks';

import { $DrawerContainer } from './Drawer.styles';
import { DrawerPosition } from './Drawer.types';

const DrawerDepthContext = React.createContext<{
  handleAddToParent: (value: number) => void;
  handleRemoveToParent: () => void;
}>({
  handleAddToParent: () => undefined,
  handleRemoveToParent: () => undefined,
});

interface DrawerContentProps {
  onClose: () => void;
  width: number;
  withPadding: boolean;
  dataTestId?: string;
  position: DrawerPosition;
}

const DrawerContent: FunctionComponent<
  React.PropsWithChildren<DrawerContentProps>
> = ({ onClose, children, width, withPadding, dataTestId, position }) => {
  const { handleAddToParent, handleRemoveToParent } =
    useContext(DrawerDepthContext);

  const [childWidths, setChildWidths] = useState<number[]>([]);

  const handleAddFromChild = useCallback(
    (value: number) => {
      setChildWidths((widths) => [value, ...widths]);
      handleAddToParent(value);
    },
    [handleAddToParent],
  );

  const handleRemoveFromChild = useCallback(() => {
    setChildWidths((widths) => widths.filter((_w, index) => index !== 0));
    handleRemoveToParent();
  }, [handleRemoveToParent]);

  useFunctionOnEscape(childWidths.length === 0 ? onClose : undefined);

  return (
    <DrawerDepthContext.Provider
      value={{
        handleAddToParent: handleAddFromChild,
        handleRemoveToParent: handleRemoveFromChild,
      }}
    >
      <$DrawerContainer
        data-testid={dataTestId}
        $withPadding={withPadding}
        $width={width}
        $widths={childWidths}
        $position={position}
      >
        {children}
      </$DrawerContainer>
    </DrawerDepthContext.Provider>
  );
};

export default DrawerContent;
